import { DateTime } from "luxon";
import { FC, MouseEvent } from "react";
import styled from "styled-components";

import Text from "@/components/Text";
import { ReleaseResource } from "@/types/resources";
import { ReleaseStatusEnum } from "@/utils/enums";
import { Time } from "./DistributionListItem";
import DistributionStatusBadge from "./DistributionStatusBadge";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import useAlerts from "@/hooks/useAlerts";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/api";

const Card = styled.div`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.lightGray};
  display: flex;
  margin: 0 -24px;
  &:last-child {
    margin-bottom: -16px;
    border: 0;
  }
`;

const ChannelInfo = styled.div`
  width: 228px;
  border-right: 0.5px solid ${({ theme }) => theme.colors.lightGray};
  padding: 16px 24px;
`;

const ConditionInfo = styled.div`
  flex: 1;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.lightGray};
  gap: 48px;
  &:last-child {
    border: 0;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusColumn = styled.div`
  display: flex;
  margin-left: auto;
  gap: 12px;
`;

const ChannelTitle = styled(Text)`
  display: flex;
  align-items: center;
  margin-top: 7px;
`;

const ChannelThumb = styled.img`
  margin-right: 8px;
  border: 1px solid rgba(144, 144, 144, 0.3);
  flex-shrink: 0;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  object-fit: cover;
`;

const ActionsDropdownWrap = styled.div`
  position: relative;
  margin-left: auto;
`;

interface Props {
  repost: ReleaseResource;
  index: number;
}

const formatNumber = (num: string) => {
  if (!num) {
    return "-";
  }

  return parseInt(num).toLocaleString().replace(/,/g, " ");
};

const DistributionRepostInformation: FC<Props> = ({ repost, index }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const renderAudienceCondition = () => {
    if (repost.conditions!.male_percentage) {
      return `Male: ${repost.conditions!.male_percentage}%`;
    }

    if (repost.conditions!.female_percentage) {
      return `Female: ${repost.conditions!.female_percentage}%`;
    }

    return "-";
  };

  const renderAudienceResults = () => {
    if (!repost.conditions!.results) {
      return "-";
    }

    if (repost.conditions!.male_percentage) {
      return `${repost.conditions!.results.male_percentage}%`;
    }

    if (repost.conditions!.female_percentage) {
      return `${repost.conditions!.results.female_percentage}%`;
    }

    return "-";
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this repost?"
      );

      if (!confirmed) return;

      await api.delete<never, { id: number }>(`/v1/releases/${repost.id}`);

      await queryClient.invalidateQueries(["releases"], {
        exact: false,
      });

      alerts.success("Repost has been successfully deleted");
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <Card>
      <ChannelInfo>
        <Text $type="smallBold">
          Posting Channel
          <Text $type="smallBold" $color="gray" as="span">
            ({index + 1})
          </Text>
        </Text>
        <ChannelTitle $type="smallNormal">
          <ChannelThumb src={repost.channel!.thumbnail} alt="Channel icon" />
          {repost.channel!.name}
        </ChannelTitle>
      </ChannelInfo>
      <ConditionInfo>
        <Row>
          <Column>
            <Text $type="smallBold">Target Views</Text>
            <Text $type="smallNormal">
              {formatNumber(repost.conditions!.views)}
            </Text>
          </Column>
          <Column>
            <Text $type="smallBold">Target Period (days)</Text>
            <Text $type="smallNormal">{repost.conditions!.period || "-"}</Text>
          </Column>
          <Column>
            <Text $type="smallBold">Audience Segmentation</Text>
            <Text $type="smallNormal">{renderAudienceCondition()}</Text>
          </Column>
          <ActionsDropdownWrap>
            <ActionsDropdown>
              <ActionsDropdownButton onClick={onDelete} as="button">
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsDropdownWrap>
        </Row>
        <Row>
          <Column>
            <Text $type="smallBold">Views Results</Text>
            <Text $type="smallNormal">
              {formatNumber(
                repost.conditions!.results?.views?.toString() ?? ""
              )}
            </Text>
          </Column>
          <Column>
            <Text $type="smallBold">Audience Segmentation Results</Text>
            <Text $type="smallNormal">{renderAudienceResults()}</Text>
          </Column>
          <StatusColumn>
            <Time $type="smallBold">
              {DateTime.fromISO(repost.releasedAt!).toFormat("dd/MM/y")} ·{" "}
              <span>
                {DateTime.fromISO(repost.releasedAt!).toFormat("HH:mm")}
              </span>
            </Time>
            <DistributionStatusBadge status={ReleaseStatusEnum.WAITING} />
          </StatusColumn>
        </Row>
      </ConditionInfo>
    </Card>
  );
};

export default DistributionRepostInformation;
